import React, { useState } from "react";
import { api, apiError } from "../lib/api";
import { useLang } from "../i18n";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import Seo from "../components/Seo";
import { Eyebrow, Reveal, GoldButton } from "../components/motion";
import { toast } from "sonner";
import { Gift } from "lucide-react";

export default function GiftCards() {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ amount: 100, recipient_name: "", recipient_email: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!user) return navigate(`/${lang}/login?next=/gift-cards`);
    setSending(true);
    try {
      const r = await api.post("/giftcards/purchase", {
        ...form,
        amount: Number(form.amount),
        origin_url: window.location.origin + `/${lang}`,
      });
      if (r.data.checkout_url) window.location.href = r.data.checkout_url;
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSending(false);
    }
  };
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="pt-[72px]">
      <Seo title={`${t("gift.title")} — Chef à Domicile`} description={t("gift.sub")} />
      <div className="max-w-[1300px] mx-auto px-4 sm:px-8 py-16 md:py-24 grid lg:grid-cols-2 gap-14 items-start">
        <Reveal>
          <Eyebrow>{t("nav.giftCards")}</Eyebrow>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-ivory mb-5">{t("gift.title")}</h1>
          <p className="text-neutral-400 leading-relaxed max-w-lg mb-10">{t("gift.sub")}</p>
          <div className="relative bg-gradient-to-br from-coal2 to-black border border-gold/30 p-10 aspect-[16/9] flex flex-col justify-between max-w-md">
            <div className="flex items-center justify-between">
              <p className="font-serif text-xl text-ivory">Chef <span className="text-gold italic">à</span> Domicile</p>
              <Gift className="text-gold" size={22} />
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-neutral-500 mb-1">Gift Card</p>
              <p className="font-serif text-4xl text-gold">€{form.amount || "—"}</p>
            </div>
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <form onSubmit={submit} className="bg-coal border border-white/8 p-8 md:p-10 space-y-5" data-testid="giftcard-form">
            <div>
              <label className="lux-label">{t("gift.amount")} *</label>
              <input data-testid="giftcard-amount-input" required type="number" min="25" max="1000" className="lux-input" value={form.amount} onChange={upd("amount")} />
              <div className="flex gap-2 mt-3">
                {[50, 100, 150, 250, 500].map((a) => (
                  <button type="button" key={a} data-testid={`giftcard-preset-${a}`} onClick={() => setForm({ ...form, amount: a })}
                    className={`text-xs px-3 py-1.5 border transition-colors ${Number(form.amount) === a ? "border-gold text-gold" : "border-white/15 text-neutral-400 hover:border-white/40"}`}>
                    €{a}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="lux-label">{t("gift.recipientName")} *</label>
              <input data-testid="giftcard-recipient-name" required className="lux-input" value={form.recipient_name} onChange={upd("recipient_name")} />
            </div>
            <div>
              <label className="lux-label">{t("gift.recipientEmail")} *</label>
              <input data-testid="giftcard-recipient-email" required type="email" className="lux-input" value={form.recipient_email} onChange={upd("recipient_email")} />
            </div>
            <div>
              <label className="lux-label">{t("gift.message")} ({t("common.optional")})</label>
              <textarea data-testid="giftcard-message" rows={3} className="lux-input" value={form.message} onChange={upd("message")} />
            </div>
            <GoldButton type="submit" disabled={sending} data-testid="giftcard-submit-btn" className="w-full">
              {sending ? t("common.sending") : t("gift.buy")}
            </GoldButton>
            <p className="text-xs text-neutral-500 leading-relaxed">{t("gift.note")}</p>
          </form>
        </Reveal>
      </div>
    </div>
  );
}
